// **React Imports
import { memo, useCallback, useEffect, useRef, useState } from 'react'

// **MUI Imports
import { Box, Typography } from '@mui/material'

// ** Third party Imports
import { Controller } from 'react-hook-form'

// **Utils Imports
import { copyToClipboard, formatDateTime } from '../../utils/utils'

// **Component Imports
import CustomChip from '../chip'
import Icon from '../icon'
import RequiredLabelComponent from '../required-label-component'

const ReadOnlyComponent = ({
  label,
  name = '',
  suffix = '',
  displayValue,
  control = null,
  showInChips = false,
  dateTimeString = false,
  needDynamicHeight = false,
  tooltipText = '',
  required = false,
  labelStyle = { fontSize: '13px' },
  valueStyles = {},
  showCopyButton = false
}) => {
  // Refs
  const contentRef = useRef(null)

  // State
  const [viewMoreButton, setViewMoreButton] = useState({
    show: false,
    more: false
  })

  // handler
  const handleExpandContent = useCallback(() => {
    setViewMoreButton((prev) => ({ ...prev, more: !prev.more }))
  }, [])

  const getValue = useCallback(
    (value) => {
      const formattedValue = dateTimeString
        ? value
          ? formatDateTime(new Date(value))
          : '-'
        : value != null
        ? value
        : '-'

      return `${formattedValue} ${value != null ? suffix : ''}`
    },
    [suffix, dateTimeString]
  )

  // useEffect to check height after render
  useEffect(() => {
    if (contentRef.current && needDynamicHeight) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const contentHeight = contentRef?.current?.scrollHeight || 99999
          setViewMoreButton({
            more: false,
            show: contentHeight > 180
          })
        }, 0)
      })
    }
  }, [displayValue, needDynamicHeight])

  return control ? (
    <Controller
      name={name}
      control={control}
      render={({ field: { value } }) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <Box sx={{ width: '100%' }}>
            {label && (
              <RequiredLabelComponent
                tooltipText={tooltipText}
                label={label}
                labelStyle={{ fontSize: '13px', ...labelStyle }}
                required={required}
              />
            )}
            {showInChips && typeof value === 'object' ? (
              <Box
                ref={contentRef}
                sx={{
                  gap: '6px',
                  display: 'flex',
                  overflow: 'hidden',
                  flexWrap: 'wrap',
                  width: '100%',
                  maxHeight: viewMoreButton.show
                    ? viewMoreButton.more
                      ? 'none'
                      : '180px'
                    : 'none'
                }}
              >
                {value?.map((chipValue) => (
                  <CustomChip size="small" key={chipValue} label={chipValue} />
                ))}
                {value?.length === 0 && '-'}
              </Box>
            ) : (
              <Typography
                ref={contentRef}
                sx={{
                  gap: '6px',
                  display: 'flex',
                  overflow: 'hidden',
                  maxHeight: viewMoreButton.show
                    ? viewMoreButton.more
                      ? 'none'
                      : '180px'
                    : 'none',
                  ...valueStyles
                }}
                title={getValue(value)}
              >
                {getValue(value)?.trim() || '-'}
              </Typography>
            )}
            {viewMoreButton.show && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '10px 0 0 0'
                }}
              >
                <Typography
                  size="small"
                  onClick={handleExpandContent}
                  sx={{
                    fontSize: '13px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'var(--primary-main)'
                  }}
                >
                  {viewMoreButton.more ? 'View Less' : 'View More'}
                </Typography>
              </Box>
            )}
          </Box>
          {showCopyButton && (
            <Box
              onClick={() => copyToClipboard(getValue(value))}
              sx={{
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                '&:hover': {
                  color: 'var(--primary-main)'
                }
              }}
            >
              <Icon
                icon={'mdi:clipboard-multiple-outline'}
                style={{ fontSize: '18px' }}
                hoverEffect
                title="Copy"
              />
            </Box>
          )}
        </Box>
      )}
    />
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      }}
    >
      <Box sx={{ width: '100%' }}>
        {label && (
          <RequiredLabelComponent
            tooltipText={tooltipText}
            label={label}
            labelStyle={{ fontSize: '13px', ...labelStyle }}
            required={required}
          />
        )}
        {showInChips && typeof displayValue === 'object' ? (
          <Box
            ref={contentRef}
            sx={{
              gap: '6px',
              display: 'flex',
              overflow: 'hidden',
              flexWrap: 'wrap',
              width: '100%',
              maxHeight: viewMoreButton.show
                ? viewMoreButton.more
                  ? 'none'
                  : '180px'
                : 'none'
            }}
          >
            {displayValue?.map((value, index) => (
              <CustomChip
                size="small"
                key={`${value}-${index}`}
                label={value}
              />
            ))}
            {displayValue?.length === 0 && '-'}
          </Box>
        ) : (
          <Typography
            ref={contentRef}
            sx={{
              gap: '6px',
              display: 'flex',
              overflow: 'hidden',
              whiteSpace: 'pre-line',
              maxHeight: viewMoreButton.show
                ? viewMoreButton.more
                  ? 'none'
                  : '180px'
                : 'none',
              ...valueStyles
            }}
            title={getValue(displayValue)}
          >
            {getValue(displayValue)}
          </Typography>
        )}
        {viewMoreButton.show && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '10px 0 0 0'
            }}
          >
            <Typography
              size="small"
              onClick={handleExpandContent}
              sx={{
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'var(--primary-main)'
              }}
            >
              {viewMoreButton.more ? 'View Less' : 'View More'}
            </Typography>
          </Box>
        )}
      </Box>

      {showCopyButton && (
        <Box
          onClick={() => copyToClipboard(getValue(displayValue))}
          sx={{
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            '&:hover': {
              color: 'var(--primary-main)'
            }
          }}
        >
          <Icon
            icon={'mdi:clipboard-multiple-outline'}
            style={{ fontSize: '18px' }}
            hoverEffect
            title="Copy"
          />{' '}
        </Box>
      )}
    </Box>
  )
}

export default memo(ReadOnlyComponent)
